import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ArrowDown, ArrowUp, ArrowUpDown } from 'lucide-react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import {
  Button,
  Card,
  CardClose,
  CardHeader,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  LoadingSpinner,
  ScrollArea,
  ScrollBar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  useToast,
} from '@ais-ui/ui-core';
import { capitalize, cn, isPresent } from '@ais-ui/utils';

import { useLocalization } from '@ais-ui/locale-management';

import {
  PiAnchor,
  PiDotsThreeVerticalBold,
  PiCheck,
  PiWarning,
  PiArrowRight,
  PiArrowSquareOut,
} from 'react-icons/pi';
import { useMapPanelContext } from '../Map/MapPanel/MapPanelContext';
import { useAISMapContext } from '../Map/AISMapContext';
import {
  useFetchTrackById,
  usePortArrivals,
  useTracksGetList,
} from '@ais-ui/api';
import { useRoutes } from './RoutesContext';
import { ErrorDisplayTable } from '../../components/ErrorDisplayTable';
import { EmptyListTable } from '../../components/EmptyListTable';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { type Ship, TrackResult } from '@ais-ui/api-spec/portserver';
import { AutoCompleteShipInfo } from '@ais-ui/websockets';
import { ToString } from '@formatjs/ecma402-abstract';
import { CreateRoute } from './Mutations/CreateRoute';
import { DeleteRoute } from './Mutations/DeleteRoute';
import { shapeToIcon } from '../Shapes/shapeToIcon';
import { CENTERCOMPONENTS } from '../Map/MapPanel/CenterComponents';
import { useSelectionState } from '../../components/SelectionStateProvider';
import {
  shapeToVectorLayer,
  useLayers,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { GeoJson } from '@ais-ui/api-spec/shapes';
import { useTableDensity } from '../utils/useTableDensity';
import { openPopupOnSecondaryScreen, PopupType } from '@ais-ui/popups';

interface TrackFetchAndDrawProps {
  id: number;
  shipName?: string;
  mmsi?: number;
}

const useTrackFetchAndDraw = ({
  id,
  shipName,
  mmsi,
}: TrackFetchAndDrawProps) => {
  const [isLoadingTrack, setIsLoadingTrack] = useState(false);

  const { addLayer, zoomToLayer } = useLayers();
  const { displayProjection, dataProjection } = useOpenLayersContext();
  const { messages } = useLocalization();
  const { toast } = useToast();

  const fetchTrackById = useFetchTrackById();

  const trackId = useMemo(() => {
    return `track-${id}`;
  }, [id]);

  const onFetchTrack = async () => {
    setIsLoadingTrack(true);
    toast({
      title: messages.genericToastTitleStarted,
      description: 'Started fetching track...',
    });

    try {
      const result = await fetchTrackById({ id, track: 1 });

      if (result && result.track && result.track.coordinates) {
        const shipNameWithFallback =
          shipName ?? mmsi?.toString() ?? `Ship ${id}`;
        const name = `${shipNameWithFallback}'s track`;

        const trackGeoJson: GeoJson = {
          type: 'Feature',
          geometry: {
            type: 'LineString',
            coordinates: result.track.coordinates,
          },
          properties: {
            id: trackId,
            name: name,
            shipName: result.source?.ship?.name || shipName,
            mmsi: result.source?.ship?.mmsi || mmsi,
            callsign: result.source?.ship?.callsign,
            shiptype: result.source?.ship?.shiptype,
            destination: result.destination?.port?.name,
            eta: result.eta,
            sog: result.source?.ship?.sog,
            color: 'yellow',
          },
        };

        const layer = shapeToVectorLayer(
          trackGeoJson,
          displayProjection,
          dataProjection,
        );

        addLayer(layer, name, true, true, true, undefined, trackId);
        zoomToLayer(layer);

        toast({
          title: messages.genericToastTitleSuccess,
          description: 'Successfully fetched track',
        });

        return result;
      } else {
        console.warn(
          'Track data is missing or incomplete in the fetch result.',
        );
        toast({
          title: messages.genericToastTitleError,
          description: messages.routesNoTrackData,
          variant: 'destructive',
        });
      }
    } catch (error) {
      console.error('Error fetching track:', error);
      toast({
        title: messages.genericToastTitleError,
        description: 'Failed to fetch track',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingTrack(false);
    }
  };

  return {
    onFetchTrack,
    isLoadingTrack,
    trackId,
  };
};

interface RoutesTableProps {
  className?: string;
}

// Updated Actions cell component with density support
interface RoutesTableActionsProps {
  arrival: TrackResult;
  density: 'normal' | 'compact';
}

const RoutesTableActions = ({ arrival, density }: RoutesTableActionsProps) => {
  const { messages } = useLocalization();
  const { toast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { setZoomToShip } = useAISMapContext();
  const { densityClasses } = useTableDensity();

  const { onFetchTrack, isLoadingTrack } = useTrackFetchAndDraw({
    id: arrival.id as number,
    shipName: arrival.source?.ship?.name,
    mmsi: arrival.source?.ship?.mmsi,
  });

  const onGoToTarget = useCallback(
    (ship?: Ship) => {
      if (!ship) {
        toast({
          title: messages.genericToastTitleError,
          description: messages.routesNoShipData,
          variant: 'destructive',
        });
        return;
      }

      const autoCompleteShip: AutoCompleteShipInfo = {
        name: ship.name ? ship.name : null,
        mmsi: ship.mmsi ? ToString(ship.mmsi) : null,
        imo: ship.imo ? ToString(ship.imo) : null,
        callSign: ship.callsign ? ship.callsign : null,
        type: 'TT_SHIP',
      };
      setZoomToShip(autoCompleteShip);
    },
    [messages, setZoomToShip, toast],
  );

  const onDelete = useCallback(() => {
    setShowDeleteDialog(true);
  }, []);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            id={`${arrival?.source?.ship?.name || 'unknown'}-dropdown-button`}
            variant="ghost"
            className={cn(densityClasses.button)}
          >
            <PiDotsThreeVerticalBold
              className={cn(density === 'compact' ? 'size-4' : 'size-5')}
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onGoToTarget(arrival?.source?.ship)}>
            View target
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => onFetchTrack()}>
            View route
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onDelete}>Delete route</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      {showDeleteDialog && (
        <DeleteRoute
          showDialog={showDeleteDialog}
          setShowDialog={setShowDeleteDialog}
          route={arrival}
        />
      )}
    </>
  );
};

// Create sortable header component with density support
interface SortableHeaderProps {
  column: any;
  children: React.ReactNode;
  density: 'normal' | 'compact';
}

const SortableHeader = ({ column, children, density }: SortableHeaderProps) => {
  const { densityClasses } = useTableDensity();

  if (!column.getCanSort()) {
    return (
      <div className={cn('font-medium', densityClasses.text)}>{children}</div>
    );
  }

  return (
    <Button
      variant="ghost"
      onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
      className={cn(
        'h-auto p-0 font-medium hover:bg-transparent justify-start',
        densityClasses.text,
      )}
    >
      {children}
      {column.getIsSorted() === 'desc' ? (
        <ArrowDown
          className={cn('ml-2', density === 'compact' ? 'h-3 w-3' : 'size-4')}
        />
      ) : column.getIsSorted() === 'asc' ? (
        <ArrowUp
          className={cn('ml-2', density === 'compact' ? 'h-3 w-3' : 'size-4')}
        />
      ) : (
        <ArrowUpDown
          className={cn('ml-2', density === 'compact' ? 'h-3 w-3' : 'size-4')}
        />
      )}
    </Button>
  );
};

export const RoutesTable = memo(({ className }: RoutesTableProps) => {
  const { tableExpanded, setTableExpanded, setCenterComponent } =
    useMapPanelContext();

  const { tableDensity, densityClasses, TruncatedCell, DateTimeCell, ShortTextCell } = useTableDensity();

  const { messages } = useLocalization();
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const {
    shape,
    showDialog: showCreateDialog,
    setShowDialog: setShowCreateDialog,
  } = useRoutes();

  const { currentPort } = useSelectionState();

  // TanStack Table sorting state
  const [sorting, setSorting] = useState<SortingState>([]);

  const {
    data: portData,
    isLoading: isPortLoading,
    isError: isPortError,
  } = usePortArrivals(currentPort?.id ? Number(currentPort.id) : undefined);

  const {
    data: trackData,
    isLoading: isTrackLoading,
    isError: isTrackError,
  } = useTracksGetList(!!shape);

  const isLoading = useMemo(() => {
    if (currentPort) {
      return isPortLoading;
    } else if (shape) {
      return isTrackLoading;
    }
    return false;
  }, [isPortLoading, isTrackLoading, currentPort, shape]);

  const isError = useMemo(() => {
    if (currentPort) {
      return isPortError;
    } else if (shape) {
      return isTrackError;
    }
  }, [isPortError, isTrackError, currentPort, shape]);

  const ShapeIcon = useMemo(() => {
    if (shape) {
      return shapeToIcon(shape);
    }
    return null;
  }, [shape]);

  const arrivals = useMemo(() => {
    if (portData && portData?.records) {
      return portData.records;
    }
    if (trackData && trackData?.records) {
      return trackData.records.filter(
        (record) => record.destination?.location?.id === shape?.properties?.id,
      );
    }
    return [];
  }, [portData, shape?.properties?.id, trackData]);

  const columns = useMemo<ColumnDef<TrackResult>[]>(
    () => [
      {
        id: 'index',
        header: '#',
        cell: ({ row }) => (
          <div className={densityClasses.cellContent}>{row.index + 1}</div>
        ),
        enableSorting: false,
        size: 50,
      },
      // 1. Name
      {
        id: 'name',
        accessorFn: (row) => row?.source?.ship?.name || '',
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            {messages.RoutesTableHeaderName}
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const shipName = capitalize(
            row.original?.source?.ship?.name
              ? row.original.source.ship.name
              : 'unknown',
          );
          return (
            <TruncatedCell
              text={shipName}
              className={cn('font-medium', densityClasses.cellContent, densityClasses.truncation.long)}
            >
              {shipName}
            </TruncatedCell>
          );
        },
      },
      // 2. MMSI
      {
        id: 'mmsi',
        accessorFn: (row) => row?.source?.ship?.mmsi || 0,
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            {messages.RoutesTableHeaderMmsi}
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const mmsi = row.original?.source?.ship?.mmsi
            ? row.original.source.ship.mmsi.toString()
            : '-';
          return (
            <ShortTextCell className={densityClasses.cellContent}>
              {mmsi}
            </ShortTextCell>
          );
        },
      },
      // 3. IMO
      {
        id: 'imo',
        accessorFn: (row) => row?.source?.ship?.imo || 0,
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            {messages.RoutesTableHeaderImo}
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const imo = row.original?.source?.ship?.imo
            ? row.original.source.ship.imo.toString()
            : '-';
          return (
            <ShortTextCell className={densityClasses.cellContent}>
              {imo}
            </ShortTextCell>
          );
        },
      },
      // 4. Callsign
      {
        id: 'callsign',
        accessorFn: (row) => row?.source?.ship?.callsign || '',
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            {messages.RoutesTableHeaderCallsign}
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const callsign = row.original?.source?.ship?.callsign || '-';
          return (
            <ShortTextCell className={densityClasses.cellContent}>
              {callsign}
            </ShortTextCell>
          );
        },
      },
      // 5. Draught
      {
        id: 'draught',
        accessorFn: (row) => row?.source?.ship?.draught || 0,
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            {messages.RoutesTableHeaderDraught}
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const draught = row.original?.source?.ship?.draught
            ? `${row.original.source.ship.draught} m`
            : '-';
          return (
            <ShortTextCell className={densityClasses.cellContent}>
              {draught}
            </ShortTextCell>
          );
        },
      },
      // 6. Flag (Country)
      {
        id: 'country',
        accessorFn: (row) => row?.source?.ship?.a2 || '',
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            Flag
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const countryCode = row.original?.source?.ship?.a2;
          return (
            <div className={densityClasses.cellContent}>
              {countryCode ? (
                <img
                  src={`/flags/f0-${countryCode.toLowerCase()}.png`}
                  alt={`Flag of ${
                    row.original?.source?.ship?.country || countryCode
                  }`}
                  className={cn(
                    'object-contain',
                    tableDensity === 'compact' ? 'max-h-4' : 'max-h-6',
                  )}
                  title={row.original?.source?.ship?.country || countryCode}
                />
              ) : (
                <span className={densityClasses.text}>-</span>
              )}
            </div>
          );
        },
      },
      // 7. ETA (Calculated)
      {
        id: 'eta',
        accessorFn: (row) => (row?.eta ? new Date(row.eta).getTime() : 0),
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            ETA (Calculated)
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const eta = row.original?.eta
            ? formatDateTimeWithTimeZone(row.original.eta)
            : '-';
          return (
            <DateTimeCell className={densityClasses.cellContent}>
              {eta}
            </DateTimeCell>
          );
        },
      },
      // 8. State with icons
      {
        id: 'state',
        accessorFn: (row) => row?.message || '',
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            State
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const message = row.original?.message || '';
          const isArrived = message?.toLowerCase() === 'arrived';
          const isDeparted = message?.toLowerCase() === 'departed';

          // Check if it's a datetime (contains numbers and colons/dashes)
          const isDateTime = /\d{4}-\d{2}-\d{2}|\d{2}:\d{2}|\d{2}\/\d{2}/.test(
            message,
          );

          let icon = null;
          let iconColor = '';

          if (isArrived) {
            icon = <PiCheck className={densityClasses.icon.medium} />;
            iconColor = 'text-green-600';
          } else if (isDeparted) {
            icon = <PiArrowRight className={densityClasses.icon.medium} />;
            iconColor = 'text-blue-600';
          } else if (!isDateTime && message && message !== 'unknown') {
            icon = <PiWarning className={densityClasses.icon.medium} />;
            iconColor = 'text-yellow-600';
          }

          return (
            <div className={densityClasses.cellContent}>
              {icon ? (
                <div
                  className={cn('flex items-center', iconColor)}
                  title={message}
                >
                  {icon}
                </div>
              ) : (
                <ShortTextCell className={densityClasses.cellContent}>
                  {message}
                </ShortTextCell>
              )}
            </div>
          );
        },
      },
      // 9. ETA (ERP)
      {
        id: 'etaErp',
        accessorFn: (row) => (row?.etaErp ? new Date(row.etaErp).getTime() : 0),
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            ETA (ERP)
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const etaErp = row.original?.etaErp
            ? formatDateTimeWithTimeZone(row.original.etaErp)
            : '-';
          return (
            <DateTimeCell className={densityClasses.cellContent}>
              {etaErp}
            </DateTimeCell>
          );
        },
      },
      // 10. Quay (ERP)
      {
        id: 'quayErp',
        accessorFn: (row) => {
          const quays =
            row?.erp?.warp?.[0]?.quay?.map((q) => q.name).join(', ') || '';
          return quays;
        },
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            Quay (ERP)
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const quays =
            row.original?.erp?.warp?.[0]?.quay?.map((q) => q.name).join(', ') ||
            '-';
          return (
            <ShortTextCell className={densityClasses.cellContent}>
              {quays}
            </ShortTextCell>
          );
        },
      },
      // 11. Color
      {
        id: 'color',
        accessorFn: (row) => row?.erp?.warp?.[0]?.color || '',
        header: () => <div className="w-0"></div>, // Empty header with minimal width
        cell: ({ row }) => {
          const color = row.original?.erp?.warp?.[0]?.color;
          const hasColor = !!color;

          return (
            <div
              className={cn('flex justify-center', densityClasses.cellContent)}
            >
              <div
                className={cn(
                  'rounded-lg border-2',
                  tableDensity === 'compact' ? 'w-5 h-5' : 'w-6 h-6',
                  hasColor ? 'border-border' : 'border-muted-foreground bg-card',
                )}
                style={{
                  backgroundColor: hasColor ? `#${color}` : 'transparent',
                }}
                title={hasColor ? `#${color}` : 'No color assigned'}
              />
            </div>
          );
        },
        enableSorting: false,
        size: tableDensity === 'compact' ? 40 : 50,
      },
      // 12. Waypoints
      {
        id: 'waypoints',
        accessorFn: (row) => row?.waypoints?.length || 0,
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            Waypoints
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const waypointCount = row.original?.waypoints?.length || 0;
          return (
            <div className={densityClasses.cellContent}>{waypointCount}</div>
          );
        },
      },
      // 13. SOG
      {
        id: 'sog',
        accessorFn: (row) => row?.source?.ship?.sog ?? 0,
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            {messages.RoutesTableHeaderSog}
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const sog = isPresent(row.original?.source?.ship?.sog)
            ? `${row.original.source.ship.sog} knots`
            : '-';
          return (
            <ShortTextCell className={densityClasses.cellContent}>
              {sog}
            </ShortTextCell>
          );
        },
      },
      // Remaining fields (shiptype, width, length)
      {
        id: 'shiptype',
        accessorFn: (row) => row?.source?.ship?.shiptype || '',
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            {messages.RoutesTableHeaderShipType}
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const shiptype = row.original?.source?.ship?.shiptype || '-';
          return (
            <ShortTextCell className={densityClasses.cellContent}>
              {shiptype}
            </ShortTextCell>
          );
        },
      },
      {
        id: 'width',
        accessorFn: (row) => row?.source?.ship?.width || 0,
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            {messages.RoutesTableHeaderWidth}
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const width = row.original?.source?.ship?.width
            ? `${row.original.source.ship.width} m`
            : '-';
          return (
            <ShortTextCell className={densityClasses.cellContent}>
              {width}
            </ShortTextCell>
          );
        },
      },
      {
        id: 'length',
        accessorFn: (row) => row?.source?.ship?.length || 0,
        header: ({ column }) => (
          <SortableHeader column={column} density={tableDensity}>
            {messages.RoutesTableHeaderLength}
          </SortableHeader>
        ),
        cell: ({ row }) => {
          const length = row.original?.source?.ship?.length
            ? `${row.original.source.ship.length} m`
            : '-';
          return (
            <ShortTextCell className={densityClasses.cellContent}>
              {length}
            </ShortTextCell>
          );
        },
      },
      // Actions column at the end
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className={densityClasses.cellContent}>
            <RoutesTableActions arrival={row.original} density={tableDensity} />
          </div>
        ),
        enableSorting: false,
      },
    ],
    [
      messages,
      formatDateTimeWithTimeZone,
      tableDensity,
      densityClasses,
      TruncatedCell,
    ],
  );

  // Memoize data for stability
  const memoizedArrivals = useMemo(() => arrivals, [arrivals]);

  // TanStack Table instance
  const table = useReactTable({
    data: memoizedArrivals,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange: setSorting,
    state: {
      sorting,
    },
  });

  const viewportRef = useRef<HTMLDivElement | null>(null);

  const { setSelectedPorts } = useSelectionState();

  const handleClose = () => {
    setCenterComponent(CENTERCOMPONENTS.NONE);
    setSelectedPorts([]);
  };

  // Use an effect to check scroll state after data loads and component renders
  useEffect(() => {
    if (!isLoading && arrivals.length > 0) {
      // Add a small delay to allow DOM to fully render the table
      const timer = setTimeout(() => {
        // Manually trigger the scroll check
        if (viewportRef.current) {
          // Dispatch a scroll event to force recalculation
          viewportRef.current.dispatchEvent(new Event('scroll'));
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [isLoading, arrivals.length]);

  // Handle opening in popup
  const handleOpenPopup = useCallback(() => {
    const params: Record<string, string | number> = {};
    if (currentPort?.id) params.portId = Number(currentPort.id);
    if (currentPort?.name) params.portName = currentPort.name;
    if (currentPort?.locode) params.portLocode = currentPort.locode;
    if (shape?.properties?.id) params.shapeId = shape.properties.id;
    if (shape?.properties?.name) params.shapeName = shape.properties.name;
    
    openPopupOnSecondaryScreen(PopupType.Routes, params);
  }, [currentPort, shape]);

  if (!tableExpanded) {
    return null;
  }

  return (
    <Card
      className={cn(
        'relative flex flex-col h-fit w-full transition-all group/data-table px-1 pb-4 pt-1',
        className,
      )}
    >
      <CardHeader
        className={cn(
          'text-lg font-normal text-muted-foreground',
          densityClasses.cardHeader,
        )}
      >
        <div className="flex justify-between items-start">
          <div>
            {currentPort && (
              <div className="flex space-x-2">
                <PiAnchor
                  className={cn(
                    'flex-shrink-0',
                    tableDensity === 'compact' ? 'size-5' : 'size-6',
                  )}
                ></PiAnchor>
                <h3 className={densityClasses.text}>{currentPort.name}</h3>
              </div>
            )}
            {shape && ShapeIcon && (
              <div className="flex space-x-2">
                <ShapeIcon
                  className={cn(
                    'flex-shrink-0',
                    tableDensity === 'compact' ? 'size-5' : 'size-6',
                  )}
                />
                <h3 className={densityClasses.text}>
                  {shape?.properties?.name
                    ? shape.properties.name
                    : 'Unknown shape'}
                </h3>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* Clear sorting button */}
            {sorting.length > 0 && (
              <Button
                variant="outline"
                size={tableDensity === 'compact' ? 'sm' : 'default'}
                onClick={() => setSorting([])}
                className={cn('text-xs', tableDensity === 'compact' && 'h-7')}
              >
                Clear Sort
              </Button>
            )}
            <Button
              id="create-route-button"
              onClick={() => setShowCreateDialog(true)}
              className={cn(
                tableDensity === 'compact' && 'h-7 px-3 text-xs',
              )}
            >
              Create
            </Button>
            <button
              id="open-routes-popup-button"
              className="rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
              onClick={handleOpenPopup}
              title="Open in new window"
            >
              <PiArrowSquareOut className={densityClasses.icon.medium} />
            </button>
            <CardClose onClick={handleClose} className="static" />
          </div>
        </div>
        <div
          className={cn(
            'text-muted-foreground',
            tableDensity === 'compact' ? 'text-xs' : 'text-sm',
          )}
        >
          {table.getRowModel().rows.length}{' '}
          {table.getRowModel().rows.length === 1 ? 'row' : 'rows'}
        </div>
      </CardHeader>
      <div className="flex overflow-hidden">
        <ScrollArea
          infiniteScrollRef={viewportRef}
          observeResize={false}
          className="mb-4 mx-2 flex-grow p-0 overflow-auto border rounded-md max-w-full"
        >
          <Table container={false}>
            <TableHeader className="sticky top-0 left-0 right-0 bg-background no-border">
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id} className="hover:bg-transparent">
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={densityClasses.tableHeader}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  ))}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow className="border-0">
                  <TableCell colSpan={table.getAllColumns().length}>
                    <div className="flex justify-center items-center min-h-[100px]">
                      <LoadingSpinner />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {isError && (
                <TableRow className="border-0">
                  <TableCell colSpan={table.getAllColumns().length}>
                    <div className="flex justify-center items-center min-h-[100px]">
                      <ErrorDisplayTable
                        srcImage="/422_not_supported_error_bro.png"
                        message="Could not fetch routes"
                        className=""
                      />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              {!isLoading &&
                !isError &&
                table.getRowModel().rows.length === 0 && (
                  <TableRow className="border-0">
                    <TableCell colSpan={table.getAllColumns().length}>
                      <div className="flex justify-center items-center min-h-[100px]">
                        <EmptyListTable
                          srcImage="empty_list_bro.png"
                          entity="route"
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              {!isLoading &&
                !isError &&
                table.getRowModel().rows.length > 0 &&
                table.getRowModel().rows.map((row) => (
                  <TableRow key={row.id} className="odd:bg-muted/50">
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        className={densityClasses.tableCell}
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </div>
      {showCreateDialog && <CreateRoute />}
    </Card>
  );
});

RoutesTable.displayName = 'RoutesTable';
